import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PlusCircle, FileText, Copy, Trash2, RefreshCw, ExternalLink, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { ANALYSES } from '@/constants/testIds';

const STATUS_LABEL = {
  pendiente: { label: 'Pendiente', color: 'bg-slate-200 text-slate-700' },
  recopilando: { label: 'Recopilando', color: 'bg-blue-100 text-blue-800' },
  analizando: { label: 'Analizando', color: 'bg-blue-100 text-blue-800' },
  generando_pdf: { label: 'Generando PDF', color: 'bg-blue-100 text-blue-800' },
  completado: { label: 'Completado', color: 'bg-green-100 text-green-800' },
  completado_con_advertencias: { label: 'Completado c/ advertencias', color: 'bg-amber-100 text-amber-800' },
  fallido: { label: 'Fallido', color: 'bg-red-100 text-red-800' },
};

const BatchAnalysisDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [stateCode, setStateCode] = useState('');
  const [munis, setMunis] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [profileId, setProfileId] = useState('');
  const [namePrefix, setNamePrefix] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/catalog/states').then((r) => setStates(r.data));
    api.get('/business-profiles').then((r) => setProfiles(r.data));
  }, [open]);
  useEffect(() => {
    if (stateCode) api.get(`/catalog/municipalities/${stateCode}`).then((r) => setMunis(r.data));
  }, [stateCode]);

  const toggle = (m) => {
    const key = `${stateCode}-${m.code}`;
    const exists = selected.find((x) => `${x.state_code}-${x.muni_code}` === key);
    if (exists) setSelected(selected.filter((x) => `${x.state_code}-${x.muni_code}` !== key));
    else setSelected([...selected, { state_code: stateCode, muni_code: m.code, muni_name: m.name }]);
  };

  const submit = async () => {
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) { toast.error('Selecciona un perfil de negocio'); return; }
    if (selected.length === 0) { toast.error('Selecciona al menos un municipio'); return; }
    if (selected.length > 25) { toast.error('Máximo 25 análisis por batch'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/analyses/batch', {
        targets: selected,
        business_profile: profile,
        name_prefix: namePrefix || `Batch ${new Date().toISOString().slice(0, 10)}`,
        radius_km: 5,
      });
      toast.success(`${r.data.created} análisis iniciados en paralelo`);
      setOpen(false); setSelected([]); onCreated?.();
      if (r.data.batch_id) {
        setTimeout(() => { window.location.href = `/batches/${r.data.batch_id}`; }, 800);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="batch-analysis-btn" variant="outline" className="h-11 border-blue-900 text-blue-900 hover:bg-blue-50">
          <Layers size={16} className="mr-2" /> Análisis en paralelo
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Análisis en paralelo (hasta 25 municipios)</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Perfil de negocio a usar *</Label>
              <Select value={profileId} onValueChange={setProfileId}>
                <SelectTrigger className="mt-1" data-testid="batch-profile"><SelectValue placeholder={profiles.length ? 'Selecciona perfil' : 'Crea uno en /profiles'} /></SelectTrigger>
                <SelectContent>{profiles.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_name} · {p.industry}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prefijo de nombre</Label><Input value={namePrefix} onChange={(e) => setNamePrefix(e.target.value)} placeholder="Ej. Expansión Norte Q1" className="mt-1" /></div>
          </div>
          <div>
            <Label>Agregar municipios por estado</Label>
            <Select value={stateCode} onValueChange={setStateCode}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
              <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {stateCode && (
            <>
              <Input placeholder="Buscar municipio…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="batch-muni-search" />
              <div className="max-h-56 overflow-y-auto border border-slate-200 p-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                  {munis.filter((m) => !filter || m.name.toLowerCase().includes(filter.toLowerCase())).slice(0, 200).map((m) => {
                    const key = `${stateCode}-${m.code}`;
                    const checked = !!selected.find((x) => `${x.state_code}-${x.muni_code}` === key);
                    return (
                      <label key={m.code} className={`flex items-center gap-2 p-2 border cursor-pointer text-xs ${checked ? 'border-blue-900 bg-blue-50' : 'border-slate-200'}`}>
                        <Checkbox checked={checked} onCheckedChange={() => toggle(m)} />
                        {m.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}
          <div>
            <div className="label-caps mb-2">Seleccionados: {selected.length}/25</div>
            <div className="flex flex-wrap gap-1">
              {selected.map((s, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-blue-50 border border-blue-200">{s.muni_name}
                  <button onClick={() => setSelected(selected.filter((_, j) => j !== i))} className="ml-2 text-red-700">×</button>
                </span>
              ))}
            </div>
          </div>
          <Button onClick={submit} disabled={submitting} data-testid="batch-submit" className="w-full bg-blue-900 hover:bg-blue-800">
            {submitting ? 'Iniciando…' : `Iniciar ${selected.length} análisis en paralelo`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function DashboardHome() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/analyses').then((r) => setItems(r.data)),
      api.get('/analyses/batches').then((r) => setBatches(r.data)).catch(() => setBatches([])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (search && !`${a.name} ${a.location?.state_name || ''} ${a.business_profile?.industry || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [items, statusFilter, search]);

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este análisis?')) return;
    await api.delete(`/analyses/${id}`);
    toast.success('Análisis eliminado');
    load();
  };
  const dup = async (id) => {
    const r = await api.post(`/analyses/${id}/duplicate`);
    toast.success('Análisis duplicado');
    nav(`/analyses/${r.data.id}`);
  };
  const regen = async (id) => {
    await api.post(`/analyses/${id}/regenerate`);
    toast.info('Regeneración iniciada');
    load();
  };

  const scoreClass = (s) => {
    if (s == null) return 'text-slate-400';
    if (s >= 75) return 'text-green-700';
    if (s >= 55) return 'text-blue-900';
    if (s >= 40) return 'text-amber-700';
    return 'text-red-700';
  };

// hex-based color used by batch cards
const scoreColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 75) return '#15803d';
  if (s >= 55) return '#1e3a8a';
  if (s >= 40) return '#b45309';
  return '#b91c1c';
};

  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-caps">Panel principal</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">
            Análisis territoriales
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Historial completo de tus análisis de inteligencia territorial. Cada análisis genera un dashboard, índices y un reporte descargable en PDF.
          </p>
        </div>
        <div className="flex gap-2">
          <BatchAnalysisDialog onCreated={load} />
          <Button asChild data-testid={ANALYSES.createBtn} className="bg-blue-900 hover:bg-blue-800 h-11 px-5">
            <Link to="/analyses/new">
              <PlusCircle size={16} className="mr-2" /> Crear análisis territorial
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <Input
          placeholder="Buscar por nombre, estado, giro…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid={ANALYSES.filterSearch}
          className="max-w-md bg-white"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56 bg-white" data-testid={ANALYSES.filterStatus}>
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {batches.length > 0 && (
        <div className="mb-6">
          <div className="label-caps mb-2">Batches de análisis en paralelo</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {batches.slice(0, 6).map((b) => (
              <Link key={b.batch_id} to={`/batches/${b.batch_id}`} data-testid={`batch-card-${b.batch_id}`} className="block bg-white border border-slate-200 p-4 hover:border-blue-900 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-900 text-sm truncate">{b.batch_name}</div>
                  <Layers size={16} className="text-blue-900 shrink-0" />
                </div>
                <div className="text-xs text-slate-500 mt-1">{b.industry || '—'}</div>
                <div className="flex items-baseline gap-3 mt-3">
                  <div>
                    <div className="label-caps">Score prom.</div>
                    <div className="font-display text-2xl font-black" style={{ color: scoreColor(b.avg_score) }}>{b.avg_score != null ? b.avg_score.toFixed(1) : '—'}</div>
                  </div>
                  <div>
                    <div className="label-caps">Progreso</div>
                    <div className="text-sm font-semibold text-slate-900">{b.completed}/{b.count}</div>
                  </div>
                  {b.failed > 0 && <div><div className="label-caps">Fallidos</div><div className="text-sm font-semibold text-red-700">{b.failed}</div></div>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200" data-testid={ANALYSES.historyTable}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3 label-caps">Nombre</th>
              <th className="px-4 py-3 label-caps">Ubicación</th>
              <th className="px-4 py-3 label-caps">Giro</th>
              <th className="px-4 py-3 label-caps">Score</th>
              <th className="px-4 py-3 label-caps">Estado</th>
              <th className="px-4 py-3 label-caps">Fecha</th>
              <th className="px-4 py-3 label-caps text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Cargando análisis…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-12 text-center">
                <div className="text-slate-500">Sin análisis todavía.</div>
                <Link to="/analyses/new" className="text-blue-900 font-semibold text-sm mt-2 inline-block hover:underline">Crear el primero →</Link>
              </td></tr>
            )}
            {filtered.map((a) => {
              const st = STATUS_LABEL[a.status] || STATUS_LABEL.pendiente;
              return (
                <tr key={a.id} data-testid={ANALYSES.row(a.id)} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{a.name}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>{a.location?.state_name}</div>
                    <div className="text-xs text-slate-500">{(a.location?.municipality_names || []).join(', ') || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.business_profile?.industry}</td>
                  <td className={`px-4 py-3 font-black text-2xl ${scoreClass(a.score_general)}`}>
                    <div className="flex items-baseline gap-2">
                      {a.score_general != null ? a.score_general.toFixed(0) : '—'}
                      {a.sensitivity_check && (
                        <span
                          data-testid={`sensitivity-badge-${a.id}`}
                          title={a.sensitivity_check.passed ? `Prueba superada · Δ ${a.sensitivity_check.delta} pts entre 6 giros diversos` : `Δ ${a.sensitivity_check.delta} pts — score poco diferenciado por giro`}
                          className={`text-[9px] font-semibold px-1.5 py-0.5 uppercase tracking-widest ${a.sensitivity_check.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                        >
                          {a.sensitivity_check.passed ? '✓' : '~'} Δ{a.sensitivity_check.delta}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 uppercase tracking-widest ${st.color}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(a.created_at).toLocaleDateString('es-MX')}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/analyses/${a.id}`} data-testid={ANALYSES.openBtn(a.id)} className="p-2 hover:bg-blue-100 text-blue-900 rounded">
                        <ExternalLink size={14} />
                      </Link>
                      {a.pdf_generated && (
                        <a href={`${api.defaults.baseURL}/analyses/${a.id}/pdf`}
                           onClick={async (e) => {
                             e.preventDefault();
                             const token = localStorage.getItem('strategia_token');
                             const r = await fetch(`${api.defaults.baseURL}/analyses/${a.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
                             const blob = await r.blob();
                             const url = URL.createObjectURL(blob);
                             const link = document.createElement('a');
                             link.href = url; link.download = `StrategIA_${a.name}.pdf`;
                             link.click();
                           }}
                           className="p-2 hover:bg-slate-100 rounded text-slate-700"
                           data-testid={`analysis-pdf-${a.id}`}
                        >
                          <FileText size={14} />
                        </a>
                      )}
                      <button data-testid={ANALYSES.duplicateBtn(a.id)} onClick={() => dup(a.id)} className="p-2 hover:bg-slate-100 rounded text-slate-700"><Copy size={14} /></button>
                      <button onClick={() => regen(a.id)} className="p-2 hover:bg-slate-100 rounded text-slate-700"><RefreshCw size={14} /></button>
                      <button data-testid={ANALYSES.deleteBtn(a.id)} onClick={() => del(a.id)} className="p-2 hover:bg-red-100 rounded text-red-700"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
